import { motion } from "framer-motion";
import { Dumbbell, UserCheck, ClipboardList, Apple, Smile, Snowflake, Lock, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";

const GYM_PHOTOS = {
  floor: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/4165d521ad29ba0d_Polished%20commercial%20gym%20interior.png",
  machines: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/ce21badf8343798c_Premium%20professional%20gym%20interior.png",
  cable: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/24bf440f0e0414a6_Polished%20premium%20gym%20interior.png",
  corridor: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/277da78931d7a4a0_Premium%20red-and-black%20gym%20interior.png",
  turf: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/41b9f77b30562402_Polished%20Professional%20Gym%20Interior-2.png",
};

const FACILITIES = [
  {
    img: GYM_PHOTOS.floor,
    label: "Main Training Floor", label_bn: "মেইন ট্রেনিং ফ্লোর",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    img: GYM_PHOTOS.machines,
    label: "Strength Machine Zone", label_bn: "স্ট্রেংথ মেশিন জোন",
    span: "",
  },
  {
    img: GYM_PHOTOS.cable,
    label: "Cable & Pulley Station", label_bn: "কেবল ও পুলি স্টেশন",
    span: "",
  },
  {
    img: GYM_PHOTOS.corridor,
    label: "Machine Corridor", label_bn: "মেশিন করিডর",
    span: "md:col-span-2",
  },
  {
    img: GYM_PHOTOS.turf,
    label: "Functional Turf Zone", label_bn: "ফাংশনাল টার্ফ জোন",
    span: "md:col-span-4",
  },
];

const CHIP_ICONS = [Dumbbell, UserCheck, ClipboardList, Apple, Smile, Snowflake, Lock, ShieldCheck];

const Facilities = () => {
  const { lang, t } = useLang();
  return (
    <section id="facilities" className="relative py-24 sm:py-32" data-testid="facilities-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt" data-testid="facilities-kicker">
              — {t.facilities_kicker}
            </p>
            <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="facilities-title">
              {t.facilities_title}
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-4" data-testid="facilities-grid">
          {FACILITIES.map((f, i) => (
            <motion.div
              key={f.label}
              data-testid={`facility-card-${i}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl border border-zinc-800 ${f.span} min-h-[280px]`}
            >
              <img
                src={f.img}
                alt={f.label}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-5 font-display text-lg font-bold uppercase tracking-widest text-white">
                <span className="mr-2 text-volt">0{i + 1}</span>
                {lang === "en" ? f.label : f.label_bn}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="facilities-chips">
          {t.facilities_chips.map((chip, i) => {
            const Icon = CHIP_ICONS[i];
            return (
              <motion.div
                key={chip}
                data-testid={`facility-chip-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-surface px-4 py-4 transition-colors duration-200 hover:border-volt/50"
              >
                <Icon className="h-5 w-5 shrink-0 text-volt" />
                <span className="text-xs font-semibold text-zinc-200 sm:text-sm">{chip}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Facilities;
